import React from "react";
import styled from "styled-components";
import { Badge, BadgeColor } from "../Badge";
import { useEvent } from "../hooks/useEvent";
import { style } from "../style";
import { Tooltip } from "../Tooltip";
import { isObject } from "../utils/isObject";
import { HeadlessTabs } from "./HeadlessTabs";

type PropTab = {
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode | { text: React.ReactNode; tooltip?: string };
  badge?: string | { text: string; color?: BadgeColor };
  "data-testid"?: string;
};

type NormalTab = {
  id: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  badge: React.ReactNode;
  "data-testid": string;
};

function normalizeTab(tab: PropTab): NormalTab {
  let subtitle: React.ReactNode = null;
  let badge: React.ReactNode = null;

  if (isObject(tab.subtitle) && "text" in tab.subtitle) {
    if (tab.subtitle.tooltip) {
      subtitle = (
        <Tooltip.Inline placement="top" content={tab.subtitle.tooltip}>
          <div>{tab.subtitle.text}</div>
        </Tooltip.Inline>
      );
    } else {
      subtitle = tab.subtitle.text;
    }
  } else if (tab.subtitle) {
    subtitle = tab.subtitle;
  }

  if (tab.badge) {
    if (isObject(tab.badge) && "text" in tab.badge && tab.badge.text) {
      badge = <Badge color={tab.badge.color}>{tab.badge.text}</Badge>;
    } else {
      badge = <Badge>{tab.badge}</Badge>;
    }
  }

  return {
    id: tab.id,
    title: tab.title,
    subtitle: subtitle,
    badge: badge,
    "data-testid": tab["data-testid"] ?? `tab-${tab.id}`,
  };
}

type GridTemplateProps = {
  $hasSubtitle: boolean;
  $hasBadge: boolean;
};

function calculateGridTemplate(props: GridTemplateProps) {
  const gridTemplateColumns = props.$hasBadge ? "auto" : "auto auto";
  const columnGap = props.$hasBadge ? style.vars.space[2] : "0px";

  const gridTemplateAreas = {
    row1: ["."] as string[],
    row2: [""] as string[],
    row3: [""] as string[],
    row4: ["."] as string[],
  };

  if (props.$hasSubtitle) {
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("subtitle");
  } else {
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("title");
  }

  if (props.$hasBadge) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("badge");
    gridTemplateAreas.row3.push("badge");
    gridTemplateAreas.row4.push(".");
  }

  return {
    gridTemplateRows: "auto auto auto auto",
    gridTemplateColumns,
    gridTemplateAreas: `
      '${gridTemplateAreas.row1.join(" ")}'
      '${gridTemplateAreas.row2.join(" ")}'
      '${gridTemplateAreas.row3.join(" ")}'
      '${gridTemplateAreas.row4.join(" ")}'
    `,
    columnGap,
    rowGap: "0px",
  };
}

const OuterTabContainer = styled.button<{ $selected: boolean }>`
  all: unset;

  display: inline-block;
  padding: 0 ${style.vars.space[4]};

  box-sizing: border-box;
  border-radius: ${style.vars.borderRadius.base};
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  position: relative;
  border: 1px solid ${style.colors.grey.dark};

  ${(props) => {
    if (props.$selected) {
      return {
        zIndex: 2,
        borderBottomColor: style.colors.white,
      };
    } else {
      return {
        borderLeftColor: "transparent",
        borderRightColor: "transparent",
        borderTopColor: "transparent",
      };
    }
  }}

  cursor: pointer;
  background: ${style.colors.white};

  &:hover {
    background: ${style.colors.grey.lightest};
  }

  &:active {
    background: ${style.colors.grey.light};
  }

  &:focus-visible {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -3px;
  }
`;

const InnerTabContainer = styled.div<{ $hasSubtitle: boolean; $hasBadge: boolean }>`
  display: grid;
  ${calculateGridTemplate}

  ${(props) => {
    /**
     * These attributes are somewhat arbitrary and just based on the design.
     */
    return { height: props.$hasSubtitle ? 61 : 40 };
  }}
`;

const TabTitle = styled.div<{ $selected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  grid-area: title;
  font-size: ${style.vars.fontSize.base};
  font-weight: ${style.vars.fontWeight.bold};
  color: ${style.colors.grey.dark};

  ${OuterTabContainer}:active &, ${OuterTabContainer}:hover & {
    color: ${style.colors.grey.darkest};
  }

  ${(props) => {
    if (props.$selected) {
      return {
        color: style.colors.grey.darkest,
      };
    }
  }}

  line-height: 1;
`;

const TabSubtitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  grid-area: subtitle;
  font-size: ${style.vars.fontSize.small};
  color: ${style.colors.grey.dark};
  line-height: 1;
`;

const TabBadge = styled.div`
  grid-area: badge;
  display: flex;
  justify-content: center;
  align-items: center;
`;

type TabProps = {
  tab: PropTab;
  selected: boolean;
  onSelect(id: String): void;
};

const Tab = React.forwardRef<HTMLButtonElement, TabProps>((props, ref) => {
  const tab = React.useMemo(() => normalizeTab(props.tab), [props.tab]);

  const onClick = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
    ev.preventDefault();
    props.onSelect(tab.id);
  });

  return (
    <OuterTabContainer
      role="tab"
      type="button"
      tabIndex={Number(props.selected) - 1}
      aria-selected={props.selected}
      $selected={props.selected}
      onClick={onClick}
      ref={ref}
      data-testid={tab["data-testid"]}
    >
      <InnerTabContainer $hasSubtitle={!!tab.subtitle} $hasBadge={!!tab.badge}>
        <TabTitle $selected={props.selected}>{tab.title}</TabTitle>
        {tab.subtitle && <TabSubtitle>{tab.subtitle}</TabSubtitle>}
        {tab.badge && <TabBadge>{tab.badge}</TabBadge>}
      </InnerTabContainer>
    </OuterTabContainer>
  );
});

const TabsContainer = styled.div`
  position: relative;
`;

const TabsLine = styled.div`
  position: absolute;
  bottom: 0;
  height: 1px;
  width: 100%;
  background: ${style.colors.grey.dark};
`;

export type TabsProps = {
  /**
   * The tabs to display to the user.
   */
  options: PropTab[];
  /**
   * The currently selected tab.
   */
  selected: string | null;
  /**
   * Called when a tab is selected by either clicking or keyboard navigation.
   * @param id The id of the tab that was selected.
   */
  onSelect: (id: string) => void;
  "data-testid"?: string;
};

export const Tabs: React.FC<TabsProps> = (props) => {
  return (
    <TabsContainer>
      <HeadlessTabs {...props}>
        {({ tab, selected, onSelect, ref }) => <Tab key={tab.id} ref={ref} tab={tab} selected={selected} onSelect={onSelect} />}
      </HeadlessTabs>
      <TabsLine />
    </TabsContainer>
  );
};
